// Ekran 09 — Stok Hareketleri.
// Sınıf adları ref_files/btys-erp-09-stok-hareketleri.html'den birebir alındı.

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { useFitInsp } from '../../lib/useFitInsp'
import { tr, trTarih, trTarihSaat } from '../../lib/trFormat'
import { ApiHatasi } from '../../lib/api'
import { useYetki, YetkiKapisi } from '../../lib/yetki'
import ManuelHareketDrawer from './ManuelHareketDrawer'
import StornoDrawer from './StornoDrawer'
import {
  hareketler,
  type BeslemeBilgisi,
  type HareketSatiri,
  type HareketTuru,
  type HareketYonu,
} from './api'

const TURLER: { anahtar: HareketTuru; etiket: string }[] = [
  { anahtar: 'tumu', etiket: 'Tümü' },
  { anahtar: 'ham', etiket: 'Hammadde' },
  { anahtar: 'urun', etiket: 'Ürün' },
  { anahtar: 'sarf', etiket: 'Sarf & Yedek' },
  { anahtar: 'yakit', etiket: 'Yakıt' },
]

const YONLER: { anahtar: HareketYonu; etiket: string }[] = [
  { anahtar: 'tumu', etiket: 'Tümü' },
  { anahtar: 'g', etiket: 'Giriş' },
  { anahtar: 'c', etiket: 'Çıkış' },
  { anahtar: 'd', etiket: 'Düzeltme/Storno' },
]

/**
 * Referans `yonEtiket()`. Storno EDİLMİŞ giriş "GİRİŞ · storno edildi" der ve
 * düzeltme rengini alır — satır zaten `.storno-r` ile soluklaşıyor (K15).
 */
function YonEtiketi({ satir }: { satir: HareketSatiri }) {
  if (satir.stornoEdildi) return <span className="yon d">GİRİŞ · storno edildi</span>
  if (satir.yon === 1) return <span className="yon g">GİRİŞ</span>
  if (satir.yon === -1) return <span className="yon c">ÇIKIŞ</span>
  return <span className="yon d">{satir.belgeTip === 'STORNO' ? 'STORNO' : 'DÜZELTME'}</span>
}

/** Referans: giriş yeşil, çıkış kırmızı, düzeltme amber; storno edilmişte renk yok. */
function miktarRengi(satir: HareketSatiri): string | undefined {
  if (satir.stornoEdildi) return undefined
  if (satir.yon === 1) return '#1F6B3A'
  if (satir.yon === -1) return 'var(--red)'
  return 'var(--amber-v)'
}

/** "15.08 15:10" — referansın tablo biçimi (yıl yok, tesis saati). */
function kisaZaman(iso: string): string {
  const tam = trTarihSaat(iso)
  if (tam === '—') return tam
  const [tarih, saat] = tam.split(' ')
  const [g, a] = tarih.split('.')
  return `${g}.${a} ${saat}`
}

/**
 * K9 fark satırı — referans: "%1,9 · tolerans içi (≤%5)".
 *
 * Eşik metne gömülmüyor, `ayar_esik`ten geliyor (K17 · §8-14); sapma eşiği
 * aşarsa hüküm de değişir — koşulsuz "tolerans içi" yazmak yanlış bilgi olurdu.
 */
function farkMetni(besleme: BeslemeBilgisi): string {
  if (besleme.farkYuzde == null) return '—'
  const yuzde = `%${tr(besleme.farkYuzde, 1)}`
  if (besleme.tolerans == null) return yuzde
  const hukum = besleme.farkYuzde <= besleme.tolerans ? 'tolerans içi' : 'tolerans dışı'
  return `${yuzde} · ${hukum} (≤%${tr(besleme.tolerans, 0)})`
}

/**
 * Hikâye günü çapası — ekran 06/07/10 ile aynı desen.
 *
 * Referans gün seçicisi TEK GÜNE (15.08) ayarlı açılıyor; burada gerçek takvim
 * günü kullanılsaydı seed 13–15.08'de sabit olduğu için tablo bomboş açılırdı.
 */
const VARSAYILAN_TARIH = '2026-08-15'

/** "15.08" — referansın gün seçicisi ve tablosu yılı yazmıyor. */
function gunAy(tarih: string): string {
  const tam = trTarih(tarih)
  return tam === '—' ? tam : tam.slice(0, 5)
}

const GUN_SECENEKLERI = [
  { deger: 'gun', etiket: `Bugün — ${gunAy(VARSAYILAN_TARIH)}` },
  { deger: 'hepsi', etiket: '13–15.08 · tümü' },
]

function Icerik() {
  const [tur, setTur] = useState<HareketTuru>('tumu')
  const [yon, setYon] = useState<HareketYonu>('tumu')
  const [arama, setArama] = useState('')
  const [gun, setGun] = useState('gun')
  const [seciliId, setSeciliId] = useState<string | null>(null)
  const panel = useFitInsp<HTMLDivElement>()
  const { ac } = useDrawer()
  const kuyruk = useQueryClient()
  const { izinVar, stornolayabilir } = useYetki()
  const yazabilir = izinVar('Hareketler', 'yaz')

  const tarih = gun === 'gun' ? VARSAYILAN_TARIH : undefined

  const { data: ozet } = useQuery({
    queryKey: ['hareket-ozeti'],
    queryFn: hareketler.ozet,
  })

  const { data: liste = [], isLoading } = useQuery({
    queryKey: ['hareketler', tur, yon, arama, gun],
    queryFn: () => hareketler.liste({ tur, yon, q: arama || undefined, tarih }),
  })

  // Referans `drawSeg()` sayaçları TÜM defter (H) üzerinden sayıyor: ne tür, ne
  // yön, ne arama, ne gün filtresi sorguya giriyor. Böylece "Çıkış"a basınca ya
  // da gün daraltılınca tür sayaçları küçülmüyor, segment sabit kalıyor.
  // Filtresiz olduğu için sorgu ekran ömrü boyunca tek kez çalışır; storno /
  // manuel kayıt sonrası ['hareketler'] geçersizleştirmesi bunu da kapsıyor.
  const { data: sayacListesi = [] } = useQuery({
    queryKey: ['hareketler', 'sayac'],
    queryFn: () => hareketler.liste({}),
  })

  const sayac = (a: HareketTuru) =>
    a === 'tumu' ? sayacListesi.length : sayacListesi.filter((s) => s.tur === a).length

  // Referans açılışta bir satırı seçili getiriyor (selH(12)) — panel boş kalmasın.
  const ilkId = liste[0]?.id
  useEffect(() => {
    if (ilkId && !liste.some((s) => s.id === seciliId)) setSeciliId(ilkId)
  }, [ilkId, liste, seciliId])

  const { data: detay } = useQuery({
    queryKey: ['hareket', seciliId],
    queryFn: () => hareketler.detay(seciliId!),
    enabled: !!seciliId,
  })

  const storno = useMutation({
    mutationFn: ({ id, gerekce }: { id: string; gerekce: string }) =>
      hareketler.storno(id, gerekce),
    onSuccess: (sonuc) => {
      kuyruk.invalidateQueries({ queryKey: ['hareketler'] })
      kuyruk.invalidateQueries({ queryKey: ['hareket-ozeti'] })
      kuyruk.invalidateQueries({ queryKey: ['hareket'] })
      toast(sonuc.mesaj)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Storno oluşturulamadı'),
  })

  return (
    <>
      <div className="crumb">
        Operasyon / Stok <b>/ Hareketler</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Stok Hareketleri</h1>
        <select className="sel" value={gun} onChange={(e) => setGun(e.target.value)}>
          {GUN_SECENEKLERI.map((g) => (
            <option key={g.deger} value={g.deger}>
              {g.etiket}
            </option>
          ))}
        </select>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            disabled
            title="Faz 7 kapsamı dışında — dışa aktarma henüz bağlanmadı"
            onClick={() => undefined}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Excel’e aktar
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Manuel stok hareketi',
                altBaslik: 'Sayım farkı / düzeltme — denetim izine işlenir (K15)',
                boyut: 'sm',
                icerik: <ManuelHareketDrawer />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Manuel hareket
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Bugün Giriş</div>
          <div className="k-v">{tr(ozet?.bugunGirisTon, 1)}</div>
          <div className="k-s">t hammadde · {ozet?.bugunGirisLot ?? '—'} lot</div>
        </div>
        <div className="k">
          <div className="k-l">Bugün Çıkış</div>
          <div className="k-v">{tr(ozet?.bugunCikisTon, 1)}</div>
          <div className="k-s">t · besleme + sevk</div>
        </div>
        <div className="k">
          <div className="k-l">Hammadde Stoku</div>
          <div className="k-v">{tr(ozet?.hammaddeStoku, 1)}</div>
          <div className="k-s">t · {ozet?.hammaddeHavuzSayisi ?? '—'} firma×ürün havuzu</div>
        </div>
        <div className="k">
          <div className="k-l">Ürün Stoku</div>
          <div className="k-v">
            {tr(ozet?.urunStokuKati, 0)} / {tr(ozet?.urunStokuSivi, 0)}
          </div>
          <div className="k-s">t katı / m³ sıvı fermente gübre</div>
        </div>
        <div className="k">
          <div className="k-l">Besleme Mutabakatı</div>
          {/* K9 · K20: sapma çürütücü beyanından geliyor; beyan günü hiç
              açılmamışsa "—" kalır — uydurma yüzde basılmaz. */}
          <div className="k-v">
            {ozet?.beslemeMutabakatiYuzde == null
              ? '—'
              : `%${tr(ozet.beslemeMutabakatiYuzde, 1)}`}
          </div>
          <div className="k-s">stok çıkışı ↔ beyan · ≤%5 (K9)</div>
        </div>
      </div>

      <div className="list-grid">
        <div className="card">
          <div className="toolbar">
            <div className="seg">
              {TURLER.map((t) => (
                <button
                  key={t.anahtar}
                  type="button"
                  className={tur === t.anahtar ? 'seg-i on' : 'seg-i'}
                  onClick={() => setTur(t.anahtar)}
                >
                  {t.etiket}
                  <small>{sayac(t.anahtar)}</small>
                </button>
              ))}
            </div>
            <div className="seg" style={{ marginLeft: 2 }}>
              {YONLER.map((y) => (
                <button
                  key={y.anahtar}
                  type="button"
                  className={yon === y.anahtar ? 'seg-i on' : 'seg-i'}
                  onClick={() => setYon(y.anahtar)}
                >
                  {y.etiket}
                </button>
              ))}
            </div>
            <div className="f-search">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Belge, kart, firma ara…"
                value={arama}
                onChange={(e) => setArama(e.target.value)}
              />
            </div>
            <div className="toolbar-right">
              <span>{liste.length} hareket</span>
            </div>
          </div>

          <table className="grid">
            <thead>
              <tr>
                {/*
                  Sütun genişlikleri gözden geçirildi: Belge iki katına çıkarken
                  kalan sabit sütunlar içeriklerinin gerektirdiği en dar ölçüye
                  çekildi ("15.08 14:05", "ÇIKIŞ", "6.180" hepsi sığıyor). Kazanç
                  genişliği sabit OLMAYAN "Stok Kartı / Havuz" sütununa gidiyor;
                  sağ panel her ekranda 396px olduğu için o sütun en çok sıkışan
                  yerdi ve kart adları ("SK-0015 Mezbaha Atığı (Karışık, Kat.3)")
                  iki üç satıra bölünüyordu.
                */}
                <th style={{ width: 78 }}>Zaman</th>
                {/*
                  Belge sütunu iki katına çıktı (132 → 264): belge no ve altındaki
                  açıklama ("BSL-2026-0815 · Besleme · günlük menü") 132px'te iki
                  satıra sarıyordu. Alan, genişliği SABİT OLMAYAN "Stok Kartı /
                  Havuz" sütunundan geliyor — o sütun kalanı paylaştığı için
                  ayrıca bir şey vermek gerekmiyor.
                */}
                <th style={{ width: 264 }}>Belge</th>
                <th>Stok Kartı / Havuz</th>
                <th style={{ width: 96 }}>İşlem</th>
                <th className="num" style={{ width: 80 }}>Miktar</th>
                <th className="num" style={{ width: 76 }}>Birim ₺</th>
                <th className="num" style={{ width: 84 }}>Bakiye</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td></tr>
              )}
              {!isLoading && liste.length === 0 && (
                <tr><td colSpan={7} className="cell-sub" style={{ padding: 14 }}>Kayıt yok.</td></tr>
              )}
              {liste.map((h) => (
                <tr
                  key={h.id}
                  className={[seciliId === h.id ? 'sel' : '', h.stornoEdildi ? 'storno-r' : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setSeciliId(h.id)}
                >
                  <td className="mono" style={{ fontSize: 11 }}>{kisaZaman(h.zaman)}</td>
                  <td>
                    <span className="blg">{h.belgeNo}</span>
                    <div className="cell-sub">{h.kaynak}</div>
                  </td>
                  <td>
                    <div className="cell-name">{h.stokKod} {h.stokAd}</div>
                    {h.havuz && <div className="cell-sub">{h.havuz}</div>}
                  </td>
                  <td><YonEtiketi satir={h} /></td>
                  <td className="num" style={{ fontWeight: 700, color: miktarRengi(h) }}>
                    {h.yon === 1 && !h.stornoEdildi ? '+' : ''}
                    {tr(h.miktar, 1)} <span className="cell-sub">{h.birim}</span>
                  </td>
                  <td className="num">{tr(h.birimDeger, 1)}</td>
                  <td className="num mono">{tr(h.bakiyeSonrasi, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="grid-foot">
            <span>
              Hareketler silinemez — düzeltme yalnız storno/revizyon ile yapılır (K15)
            </span>
          </div>
        </div>

        {/* ---- inceleme paneli ---- */}
        <aside className="insp card" ref={panel}>
          {!detay ? (
            <div className="insp-empty">
              Bir hareket seçin —<br />belge zinciri burada görünür.
            </div>
          ) : (
            <>
              <div className="insp-h">
                <div className="insp-eyebrow">
                  <span className="blg">{detay.satir.belgeNo}</span>
                  <span>·</span>
                  <span>{kisaZaman(detay.satir.zaman)}</span>
                </div>
                <div className="insp-t">
                  {detay.satir.stokKod} {detay.satir.stokAd}
                </div>
                <div className="cell-sub" style={{ marginTop: 2 }}>
                  {detay.satir.kaynak}
                  {detay.satir.havuz ? ` — ${detay.satir.havuz}` : ''}
                </div>
              </div>

              <div className="insp-b">
                <div className="insp-sec">
                  <div className="inst">
                    <div className="inst-t">Hareket</div>
                    <div className="inst-3">
                      <div className="inst-c">
                        <div className="inst-v">
                          {detay.satir.yon === 1 && !detay.satir.stornoEdildi ? '+' : ''}
                          {tr(detay.satir.miktar, 1)}
                        </div>
                        <div className="inst-l">{detay.satir.birim}</div>
                      </div>
                      <div className="inst-c">
                        <div className="inst-v">{tr(detay.satir.birimDeger, 1)}</div>
                        <div className="inst-l">₺/{detay.satir.birim} (K12)</div>
                      </div>
                      <div className="inst-c">
                        <div className="inst-v">{tr(detay.satir.bakiyeSonrasi, 1)}</div>
                        <div className="inst-l">
                          {detay.satir.havuz ? 'havuz bakiyesi' : 'kart bakiyesi'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- LOT: belge zinciri (K6 vitrini) --- */}
                {detay.lot && (
                  <div className="insp-sec">
                    <div className="insp-sec-t">Belge Zinciri</div>
                    <div className="mini-list">
                      {/* Referans bu satırları KOŞULSUZ basıyor ve veri yoksa
                          "undefined" yazıyor; eksik olan satır hiç çizilmiyor. */}
                      {detay.lot.plaka && (
                        <div className="mini-li">
                          Araç / sefer
                          <span className="vv">
                            {detay.lot.plaka}
                            {detay.lot.surucu ? ` · ${detay.lot.surucu}` : ''}
                            {detay.lot.seferEtiketi ? ` · ${detay.lot.seferEtiketi}` : ''}
                          </span>
                        </div>
                      )}
                      {detay.lot.numuneEtiketi && (
                        <div className="mini-li">
                          Numune<span className="vv">{detay.lot.numuneEtiketi}</span>
                        </div>
                      )}
                      {detay.lot.haoSonrasi != null && (
                        <div className="mini-li">
                          Havuz HAO (K6)
                          <span className="vv">
                            {tr(detay.lot.haoOncesi, 1)} → <b>{tr(detay.lot.haoSonrasi, 1)}</b> ₺/t
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* --- storno edilmiş orijinal --- */}
                {detay.satir.stornoEdildi && detay.storno && (
                  <div className="insp-sec">
                    <div
                      className="info-box"
                      style={{
                        background: 'var(--amber-bg)',
                        borderColor: 'var(--amber-bd)',
                        color: 'var(--amber)',
                      }}
                    >
                      <div>
                        Bu giriş storno edildi — ters kaydı: <b>{detay.storno.tersBelgeNo}</b>.
                        Kayıt denetim izinde soluk görünür, silinmez (K15).
                      </div>
                    </div>
                  </div>
                )}

                {/* --- ters kaydın kendisi --- */}
                {detay.satir.belgeTip === 'STORNO' && detay.storno && (
                  <div className="insp-sec">
                    <div
                      className="info-box"
                      style={{
                        background: 'var(--amber-bg)',
                        borderColor: 'var(--amber-bd)',
                        color: 'var(--amber)',
                      }}
                    >
                      <div>
                        <b>Storno — {detay.storno.orijinalBelgeNo}</b>
                        <br />
                        {detay.storno.gerekce ?? 'Kullanıcı stornosu — orijinal kayıt korunur.'}
                      </div>
                    </div>
                  </div>
                )}

                {/* --- BSL: menü kalemleri + K9 mutabakatı --- */}
                {detay.besleme && (
                  <>
                    <div className="insp-sec">
                      <div className="insp-sec-t">
                        Menü Kalemleri — {detay.satir.belgeNo}
                      </div>
                      <div className="mini-list">
                        {detay.besleme.kalemler.map((k, i) => (
                          <div className="mini-li" key={`${k.etiket}-${i}`}>
                            {k.etiket}
                            <span className="vv">−{tr(k.miktar, 1)} {k.birim}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* K19 — referansta bu bölümün başlığı YOK, çıplak mini-list. */}
                    {detay.besleme.hijyen && (
                      <div className="insp-sec">
                        <div className="mini-list">
                          <div className="mini-li">
                            Hijyenizasyon<span className="vv">{detay.besleme.hijyen}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="insp-sec">
                      <div className="inst">
                        <div className="inst-t">Besleme Mutabakatı (K9 · K20)</div>
                        <div className="inst-row">
                          <span className="inst-l">Stok çıkışı — menü Σ</span>
                          <span className="inst-v">{tr(detay.besleme.stokCikisi, 1)} t</span>
                        </div>
                        <div className="inst-row">
                          <span className="inst-l">Beyan — çürütücü beslenen Σ</span>
                          <span className="inst-v">
                            {detay.besleme.beyanToplami == null
                              ? '—'
                              : `${tr(detay.besleme.beyanToplami, 1)} t`}
                          </span>
                        </div>
                        <div className="inst-row">
                          <span className="inst-l">Fark</span>
                          <span className="inst-v">{farkMetni(detay.besleme)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* --- SVK: araç / irsaliye / alıcı (K14) --- */}
                {/* Referansta liste ve not AYNI .insp-sec içinde; not `.pnote`
                    olarak listenin altına 6px boşlukla giriyor. */}
                {detay.sevk && (
                  <div className="insp-sec">
                    <div className="mini-list">
                      {detay.sevk.arac && (
                        <div className="mini-li">
                          Araç / sürücü<span className="vv">{detay.sevk.arac}</span>
                        </div>
                      )}
                      {detay.sevk.irsaliye && (
                        <div className="mini-li">
                          İrsaliye<span className="vv">{detay.sevk.irsaliye}</span>
                        </div>
                      )}
                      {detay.sevk.alici && (
                        <div className="mini-li">
                          Alıcı<span className="vv">{detay.sevk.alici}</span>
                        </div>
                      )}
                    </div>
                    {detay.not && (
                      <div className="pnote" style={{ marginTop: 6 }}>{detay.not}</div>
                    )}
                  </div>
                )}

                {/* --- serbest not (sarf / düzeltme / yakıt) --- */}
                {detay.not && !detay.storno && !detay.sevk && (
                  <div className="insp-sec">
                    <div className="pnote" style={{ margin: 0 }}>{detay.not}</div>
                  </div>
                )}
              </div>

              <div className="insp-f">
                {detay.stornoEdilebilir && (
                  <button
                    className="btn btn-danger btn-sm"
                    type="button"
                    disabled={!stornolayabilir}
                    title={
                      stornolayabilir
                        ? undefined
                        : 'K15: storno için ayrıca yetkilendirilmeniz gerekir'
                    }
                    onClick={() =>
                      ac({
                        baslik: 'Storno oluştur',
                        altBaslik: `${detay.satir.belgeNo} · ters kayıt üretilir (K15)`,
                        boyut: 'sm',
                        icerik: (
                          <StornoDrawer
                            belgeNo={detay.satir.belgeNo}
                            bekliyor={storno.isPending}
                            onay={(gerekce) => storno.mutate({ id: detay.satir.id, gerekce })}
                          />
                        ),
                      })
                    }
                  >
                    Storno oluştur
                  </button>
                )}
                <button
                  className="btn btn-pri btn-sm"
                  type="button"
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => toast(`Kaynak belge açılıyor — ${detay.satir.belgeNo}`)}
                >
                  Kaynak belgeyi aç
                </button>
              </div>
            </>
          )}
        </aside>
      </div>
    </>
  )
}

export default function HareketlerSayfasi() {
  return (
    <Kabuk listeBasligi="Stok Hareketleri">
      <YetkiKapisi modul="Hareketler">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
